import { Group, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { InitiativeStatus } from '../../api/initiative-client/initiative-client.type';
import type { Outcome } from '../../api/strategy-client/strategy-client.type';
import { getStatusLabel } from '../../containers/process-analysis/filters/initiative-overview/initiative-overview.helpers';
import { formatDate } from '../../helpers/date-time-helpers/formatting/formatting';
import { baseWhite, inkLighter, skyDark } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';

export function RiskAnalysisOutcomeSideSection({ outcome, onClose }: { outcome: Outcome | null; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleMoreDetailsClick = () => {
    if (!outcome) return;
    navigate(`/application/strategy/outcomes/${outcome.id}`);
  };

  return (
    <Stack>
      <Group justify="space-between" style={{ position: 'relative' }}>
        <Group gap="xs" style={{ marginRight: '32px' }}>
          <Text weight="bold" size="medium">
            {outcome?.name}
          </Text>
        </Group>
        <Icon
          name="close"
          onClick={onClose}
          size={20}
          color={skyDark}
          style={{ position: 'absolute', right: 0, top: 0 }}
        />
      </Group>
      <Divider />
      <Stack gap="xs">
        {[
          { label: 'Start Date', value: formatDate(outcome?.start_date) },
          { label: 'Activity Start Date', value: formatDate(outcome?.activity_start_date) },
          { label: 'End Date', value: formatDate(outcome?.end_date) },
          { label: 'Activity End Date', value: formatDate(outcome?.activity_end_date) },
          { label: 'Progress', value: `${Math.round(outcome?.percent_progress ?? 0)}%` },
          {
            label: 'Status',
            value: getStatusLabel(
              (outcome?.status?.by_tasks?.status as InitiativeStatus | undefined) ?? InitiativeStatus.Unknown,
              t,
            ),
          },
          { label: 'Risk Score', value: outcome?.flora_annotations?.risk_score ?? '-' },
        ].map(({ label, value }) => (
          <Group key={label} justify="space-between">
            <Text size="small" color={inkLighter}>
              {label}
            </Text>
            <Text size="small" color={inkLighter}>
              {value}
            </Text>
          </Group>
        ))}
      </Stack>
      <Divider />
      <Button
        variant="secondary"
        size="sm"
        radius="xl"
        leftSection={<Icon name="visibility" size={16} color={baseWhite} />}
        onClick={handleMoreDetailsClick}
        disabled={!outcome}
      >
        More Outcome Details
      </Button>
      <Divider />
      <Text weight="medium" size="medium">
        Risk Summary
      </Text>
      <Text size="small">{outcome?.flora_annotations?.executive_summary || 'N/A'}</Text>
      <Divider />
      <Text weight="medium" size="medium">
        Action Plan
      </Text>
      <Text size="small">{outcome?.flora_annotations?.action_plan || 'N/A'}</Text>
    </Stack>
  );
}
